import { Button } from "@mui/material";
import Alert from "components/alert";
import NotAuthorized from "components/notAuthorized";
import useApiCall from "hooks/useApiCall";
import useBase from "hooks/useBase";
import usePermissions from "hooks/usePermissions";
import useQueries from "hooks/useQueries";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { deleteInvoice, viewAllInvoice } from "../api/invoices";
import LeadsBulkActions from "./BulkActions";
import Header from "./Header";
import Summary from "./Summary";
import AddEdit from "./addEdit/AddEdit";
import AdvanceFilter from "./filter/AdvanceFilter";
import MainScreenFilter from "./filter/MainScreenFilter";
import MainTable from "./table/MainTable";
import ViewInvoice from "./viewInvoice/ViewInvoice";
import { getQueryFilterMap } from "../utils/getQueryFilterMap";

const Invoices = () => {
  const { queries, setQueries, openAddEdit, openView } = useQueries();
  const filteringQueries = useMemo(
    () => queries?.get("filter") || {},
    [queries]
  );
  const [persistedData, setPersistedData] = useState({});
  const { updateBase, base } = useBase();
  const [activeMenu, setActiveMenu] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]);
  // Helper states
  const [reload, setReload] = useState(false);

  // Initialize filters with URL query params
  const getInitialFilters = () => {
    if (!filteringQueries) return {};

    const queryFilter = getQueryFilterMap(filteringQueries);
    return queryFilter ? { ...queryFilter } : {};
  };

  const [filters, setFilters] = useState(getInitialFilters());
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
  });

  const isFiltering = () => {
    let filtering = false;
    for (const key in filters) {
      if (Array.isArray(filters[key])) {
        if (filters[key].length > 0) filtering = true;
      } else {
        if (filters[key]) filtering = true;
      }
    }
    return filtering;
  };

  const { data, loading, fetchData: fetchListData } = useApiCall();

  useEffect(() => {
    updateBase([
      "customers",
      "assignees",
      "intakes",
      "projects",
      "fees",
      "currencies",
      "paymentMethods",
      "invoices",
    ]);
  }, [reload]);

  useEffect(() => {
    let finalFilters = { ...filters };
    let finalPagination = { ...pagination };
    finalFilters.search = search;
    fetchListData(
      viewAllInvoice,
      {
        body_data: finalFilters,
        params: `page=${finalPagination?.current_page}&limit=${finalPagination?.per_page}`,
      },
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, search, pagination?.current_page, pagination?.per_page]);

  useEffect(() => {
    if (data) {
      const { data: allData, ...rest } = data;
      setAllInvoices(allData);
      setPagination(rest);
      setSelectedRows([]);
    }
  }, [data]);

  // Sync filters with URL query params
  useEffect(() => {
    if (!filteringQueries) return;

    const newFilters = getQueryFilterMap(filteringQueries);
    if (newFilters) {
      setFilters({ ...newFilters });
      setReload((pre) => !pre);
    }
  }, [filteringQueries]);

  const [openAlert, setOpenAlert] = useState(false);

  const handleDelete = async (id) => {
    setOpenAlert(id);
  };

  const handleDeleteConfirm = async () => {
    if (allInvoices?.length > 1) {
      setAllInvoices(
        allInvoices.filter((invoice) => invoice?.id !== openAlert)
      );
      setOpenAlert(false);
      try {
        const result = await deleteInvoice({ id: openAlert });
        if (result.success) {
          toast.success(result?.message);
        } else {
          toast.error(result?.message);
        }
      } catch (error) {}
    } else {
      setOpenAlert(false);
      const result = await deleteInvoice({ id: openAlert });
      if (result.success) {
        toast.success(result?.message);
      } else {
        toast.error(result?.message);
      }
      setReload((pre) => !pre);
    }
    setSelectedRows([]);
  };

  const permissions = usePermissions("invoice");

  const all_props = {
    ...base,
    persistedData,
    setPersistedData,
    setReload,
    filters,
    setFilters,
    isFiltering,
    pagination,
    setPagination,
    allInvoices,
    setAllInvoices,
    permissions,
  };

  // Bulk options
  const [selectedRows, setSelectedRows] = useState([]);

  const handleTableCheckBox = (id, checked) => {
    if (id === "all") {
      if (checked) {
        setSelectedRows(allInvoices?.map((invoice) => invoice?.id));
      } else {
        setSelectedRows([]);
      }
    } else {
      if (checked) {
        setSelectedRows([...selectedRows, id]);
      } else {
        setSelectedRows(selectedRows?.filter((invoice) => invoice !== id));
      }
    }
  };

  return (
    <div>
      {openAlert && (
        <Alert
          open={openAlert}
          handleClose={setOpenAlert}
          title="Delete Invoice"
          desc="Are you sure you want to delete? Don't worry you will find this invoice on Trash Menu"
          buttons={
            <>
              <Button onClick={() => setOpenAlert(false)}>No, keep it</Button>
              <Button variant="solid" onClick={handleDeleteConfirm}>
                Yes, delete it
              </Button>
            </>
          }
        />
      )}

      <Header
        {...{
          activeMenu,
          setActiveMenu,
          setOpenFilter,
          setSearch,
          isFiltering,
          setPagination,
          pagination,
          permissions,
          search,
          filters,
        }}
      />
      {activeMenu === "summary" && (
        <Summary filters={filters} search={search} />
      )}
      {openFilter && (
        <AdvanceFilter
          openDrawer={openFilter}
          setOpenDrawer={setOpenFilter}
          {...all_props}
        />
      )}
      {openAddEdit && (
        <AddEdit
          {...{
            openDrawer: openAddEdit,
            setOpenDrawer: () => setQueries(""),
            ...all_props,
          }}
        />
      )}
      {openView && (
        <ViewInvoice
          openDrawer={openView}
          setOpenDrawer={() => setQueries("")}
          {...all_props}
        />
      )}
      {!activeMenu &&
        !selectedRows?.length > 0 &&
        !!(permissions["list(own)"] || permissions["list(global)"]) && (
          <MainScreenFilter {...all_props} />
        )}
      {selectedRows?.length > 0 && (
        <LeadsBulkActions
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          {...all_props}
        />
      )}
      {!activeMenu &&
        (permissions["list(own)"] || permissions["list(global)"] ? (
          <MainTable
            handleDelete={handleDelete}
            loading={loading}
            handleTableCheckBox={handleTableCheckBox}
            selectedRows={selectedRows}
            {...all_props}
          />
        ) : (
          <NotAuthorized />
        ))}
    </div>
  );
};

export default Invoices;
